import { useQuery } from "@tanstack/react-query";
import { getOptionSources } from "../api";
import { OptionSource } from "../dtos";

/**
 * §OptionSources: the tenant's option-source registry, shared by every `select` that binds through
 * `optionsFrom`. It is tenant CONFIG (developers, compounds, property types…), identical for every
 * step and every case, so it is fetched once and cached long — a phase full of bound selects must
 * not fan out one request per field.
 *
 * Deliberately NOT under the "nawy-now" query key: a step submit invalidates that whole tree, and
 * re-fetching static config on every save is waste.
 */
export const useOptionSources = () =>
  useQuery({
    queryKey: ["nawy-now-option-sources"],
    queryFn: getOptionSources,
    staleTime: 30 * 60 * 1000,
    cacheTime: 60 * 60 * 1000,
  });

/**
 * §Other: the escape hatch every registry-bound select offers — a developer or compound the tenant's
 * config does not list. It belongs to no parent, so it survives dependent narrowing: a developer with no
 * registered compounds still offers it.
 *
 * The literal has to match workflow-manager's own `OTHER_OPTION` exactly, because the template's
 * conditions compare against this string to reveal the follow-up "… (other)" fields. It is a contract
 * value, not a label — do not localise or prettify it.
 */
export const OTHER_OPTION = "Other";

/**
 * Resolve one source's selectable values. A source with `dependsOn` is narrowed by the parent's
 * current value (compounds under the chosen developer); with no parent picked yet it returns an
 * EMPTY list rather than the full one — offering every compound before a developer is chosen is how
 * you get a Palm Hills lead pointed at a SODIC compound.
 *
 * Note this returns the REGISTRY's values only — `OTHER_OPTION` is appended by the renderer, so callers
 * that need to know whether the tenant actually configured anything (the "no options are configured yet"
 * and "choose a developer first" hints) can still tell.
 */
export const resolveOptions = (
  sources: OptionSource[] | undefined,
  optionsFrom: string | undefined,
  parentValue?: unknown
): { options: string[]; dependsOn?: string; ready: boolean } => {
  if (!optionsFrom) return { options: [], ready: false };
  const source = sources?.find((entry) => entry.key === optionsFrom);
  if (!source) return { options: [], ready: false };
  if (!source.dependsOn) return { options: source.options ?? [], ready: true };
  const parent = typeof parentValue === "string" ? parentValue : "";
  if (!parent) {
    return { options: [], dependsOn: source.dependsOn, ready: true };
  }
  return {
    options: source.optionsByParent?.[parent] ?? [],
    dependsOn: source.dependsOn,
    ready: true,
  };
};
